import { cn } from 'cn'
import useTheme from 'lib/theme/useTheme'

import EditCircleIcon from 'components/icons/EditCircleIcon'
import CheckIconBlack from 'components/icons/CheckIconBlack'
import CircleIcon from 'components/icons/CircleIcon'

const widthFractionClass: Record<number, string> = {
  2: 'w-1/2',
  3: 'w-1/3',
  4: 'w-1/4'
}

const StepCircle = ({
  stepNumber,
  isComplete,
  isCurrent,
  dark
}: {
  stepNumber: number
  isComplete: boolean
  isCurrent: boolean
  dark?: boolean
}) => {
  if (isComplete) {
    return (
      <div
        className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-orange-500'
        )}
      >
        <svg
          width="18"
          height="14"
          viewBox="0 0 18 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 6.5L6.5 12L17 1.5"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    )
  }

  if (isCurrent) {
    return (
      <div
        className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-orange-500'
        )}
      >
        <span className="text-base font-bold text-white">{stepNumber}</span>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2',
        dark ? 'border-white/30 bg-white/10' : 'border-gray-300 bg-gray-50'
      )}
    >
      <span
        className={cn(
          'text-base font-bold',
          dark ? 'text-white/40' : 'text-gray-400'
        )}
      >
        {stepNumber}
      </span>
    </div>
  )
}

const FormSteps = ({
  step: currStepValue,
  allSteps
}: {
  step: string
  allSteps: { name: string; value: string }[]
}) => {
  const { theme } = useTheme()
  const dark = theme.page.themedBackground
  const currentTab = allSteps.find((t) => t.value === currStepValue)!
  const currentIndex = allSteps.indexOf(currentTab)
  return (
    <div className="pb-8 sm:pt-4">
      <div className="sm:block hidden">
        <ol className="flex w-full">
          {allSteps.map((s, index) => {
            const isComplete = currentIndex > index
            const isCurrent = currentIndex === index
            const isFirst = index === 0
            const isLast = index === allSteps.length - 1

            const leftLineActive = currentIndex >= index
            const rightLineActive = currentIndex > index

            return (
              <li key={s.name} className="flex-1 flex flex-col items-center">
                <div className="flex items-center w-full">
                  {!isFirst ? (
                    <div
                      className={cn(
                        'flex-1 h-0.5',
                        leftLineActive ? 'bg-orange-500' : 'bg-gray-300'
                      )}
                    />
                  ) : (
                    <div className="flex-1" />
                  )}

                  <StepCircle
                    stepNumber={index + 1}
                    isComplete={isComplete}
                    isCurrent={isCurrent}
                    dark={dark}
                  />

                  {!isLast ? (
                    <div
                      className={cn(
                        'flex-1 h-0.5',
                        rightLineActive ? 'bg-orange-500' : 'bg-gray-300'
                      )}
                    />
                  ) : (
                    <div className="flex-1" />
                  )}
                </div>

                <span
                  className={cn(
                    'mt-2 text-sm font-theme-heading font-bold text-center',
                    dark
                      ? {
                          'text-white': isCurrent || isComplete,
                          'text-white/40': !isCurrent && !isComplete
                        }
                      : {
                          'text-gray-900': isCurrent || isComplete,
                          'text-gray-400': !isCurrent && !isComplete
                        }
                  )}
                >
                  {s.name}
                </span>
              </li>
            )
          })}
        </ol>
      </div>

      <div className="sm:hidden -mx-4">
        <ol className="flex justify-between">
          {allSteps.map((s) => {
            const isComplete =
              allSteps.indexOf(currentTab) > allSteps.indexOf(s)
            const isCurrent =
              allSteps.indexOf(currentTab) === allSteps.indexOf(s)

            return (
              <li
                key={s.name}
                className={cn(
                  'flex flex-col',
                  widthFractionClass[allSteps.length] || 'w-1/3'
                )}
              >
                <div className="flex w-full justify-between items-center">
                  <div className="relative flex-1 grow">
                    <div
                      aria-hidden="true"
                      className="absolute z-10 inset-0 flex items-center"
                    >
                      <div
                        className={cn('h-0.5 w-full', {
                          [dark ? 'bg-white/30' : 'bg-gray-300']:
                            !isComplete && !isCurrent,
                          [dark ? 'bg-white' : 'bg-theme-primary']:
                            isComplete || isCurrent
                        })}
                      />
                    </div>
                  </div>

                  <div className="">
                    {isCurrent && <EditCircleIcon dark={dark} />}
                    {isComplete && <CheckIconBlack dark={dark} />}
                    {!isComplete && !isCurrent && <CircleIcon dark={dark} />}
                  </div>

                  <div className="relative flex-1">
                    <div
                      aria-hidden="true"
                      className="absolute z-10 inset-0 flex items-center"
                    >
                      <div
                        className={cn('h-0.5 w-full', {
                          [dark ? 'bg-white/30' : 'bg-gray-300']:
                            !isComplete && !isCurrent,
                          [dark ? 'bg-white' : 'bg-theme-primary']:
                            isComplete || isCurrent
                        })}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-1">
                  <div
                    className={cn(
                      'w-24 mx-auto text-center text-sm font-theme-heading font-bold',
                      dark
                        ? {
                            'text-white': isCurrent || isComplete,
                            'text-white/40': !isCurrent && !isComplete
                          }
                        : {
                            'text-gray-900': isCurrent || isComplete,
                            'text-gray-400': !isCurrent && !isComplete
                          }
                    )}
                  >
                    {s.name}
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}

export default FormSteps
